import dayjs from 'dayjs';
import { ASSET_IMAGE_URL_PREFIX } from '../constants/equipment';
import { get } from 'lodash-es';
import { PART_ASSIGNMENT_KEYS } from '../constants/order';
import { ShowActiveRentalItem, ShowOrderItem } from '../types/order';
import {
  PartAssignment,
  ShowActiveRentalItemDTO,
  ShowEquipmentItemDTO,
  ShowOrderItemDTO,
} from '../types/order-item';

export const mapToShowOrderItem = (data: ShowOrderItemDTO): ShowOrderItem => {
  return {
    orderId: `${data.order_id ?? ""}${data.quote_id ?? ""}`,
    orderPlaced: dayjs(data.created_at).valueOf(),
    jobsite: [
      data.jobsite_address?.address_line1,
      data.jobsite_address?.city,
      data.jobsite_address?.state,
      data.jobsite_address?.zipcode,
    ].filter(Boolean).join(", "),
    tenureStartDate: dayjs(data.start_date).valueOf(),
    tenureEndDate: dayjs(data.end_date).valueOf(),
    equipments: data.line_items.map((item: ShowEquipmentItemDTO) => ({
      equipment: item.equipment_name,
      quantity: item.quantity,
    })),
    statusId: data.order_status_id ?? 10
  };
};

export const rentalIsBulkItem = (rental: ShowActiveRentalItemDTO) => {
  return Array.isArray(rental.part_assignments) && rental.part_assignments.length > 0;
};

export const getLatestPartAssignment = (rental: ShowActiveRentalItemDTO) => {
  if (!rentalIsBulkItem(rental)) {
    return null;
  }

  return rental.part_assignments[rental.part_assignments.length - 1];
};

export const getPartName = (latestPartAssignment: PartAssignment | null) => {
  if (!latestPartAssignment) {
    return '';
  }

  const providerName = get(latestPartAssignment, PART_ASSIGNMENT_KEYS.PART_PROVIDER_NAME, '');
  const partDescription = get(
    latestPartAssignment,
    PART_ASSIGNMENT_KEYS.PART_CLASS_DESCRIPTION,
    '',
  );

  const partName = `${providerName} ${partDescription}`.trim();

  return partName;
};

export const getPartNumber = (latestPartAssignment: PartAssignment | null) => {
  if (!latestPartAssignment) {
    return null;
  }

  const defaultPartNumber = get(latestPartAssignment, PART_ASSIGNMENT_KEYS.ID, '');
  const partNumber = get(latestPartAssignment, PART_ASSIGNMENT_KEYS.PART_NUMBER, '');

  return Number(partNumber || defaultPartNumber);
};

export const mapToShowActiveRentalItem = (data: ShowActiveRentalItemDTO): ShowActiveRentalItem => {
  const latestPartAssignment: PartAssignment | null = getLatestPartAssignment(data);
  const latestPartAssignmentData = latestPartAssignment
    ? {
        partName: getPartName(latestPartAssignment),
        partNumber: getPartNumber(latestPartAssignment),
      }
    : undefined;
  return {
    imageUrl: data.equipment?.photo?.filename
      ? `${ASSET_IMAGE_URL_PREFIX}/${data.equipment.photo?.filename}`
      : null,
    equipment: {
      className: data.equipment_class?.name || data.equipment?.equipment_class?.name || '',
      classId: data.equipment?.equipment_class?.equipment_class_id ?? null,
      categoryName: data.equipment?.category?.name ?? '',
      categoryId: data.equipment?.category?.category_id ?? '',
      makeName: data.equipment?.make?.name ?? '',
      makeId: data.equipment?.make?.equipment_make_id ?? '',
      slug: null,
      description: data.equipment?.description,
    },
    isAssetPresent: data.equipment?.asset_id ? true : false,
    model: data.equipment?.model ?? '',
    assetId: data.equipment?.asset_id,
    latestPartAssignment: latestPartAssignmentData,
    locationId: data.current_location?.location_id,
    startDate: data.start_date,
    endDate: data.end_date,
    jobsiteName: data.current_location?.nickname,
    jobsiteAddress: `${data.current_location.street_1}, ${data.current_location.city}, ${data.current_location.state.abbreviation} ${data.current_location.zip_code}`,
    rentalId: data.rental_id,
    orderId: data.order.order_id,
    off_rent_date_requested: data.off_rent_date_requested,
    purchase_order: {
      name: data.order?.purchase_order?.name ?? '',
      purchase_order_id: data.order?.purchase_order?.purchase_order_id ?? '',
      purchase_order_start_date: data.order?.purchase_order?.start_date ?? '',
      purchase_order_end_date: data.order?.purchase_order?.end_date ?? '',
      budget_amount: data.order?.purchase_order?.budget_amount ?? '',
    },
    service_location_city: data.current_location?.city,
    market_id: data.order?.market_id ?? '',
    rental_type_id: data?.rental_type_id,
  };
};
